import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Anchor, Wrench, Ship, Award, Star, MapPin, Phone, Clock, CheckCircle2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageSlot } from "@/components/ImageSlot";
import { BoatCard } from "@/components/public/BoatCard";
import { LeadDialog } from "@/components/public/LeadDialog";
import { Seo } from "@/components/Seo";
import { api, fileUrl } from "@/lib/api";

const Eyebrow = ({ children }) => (
  <span className="text-xs font-bold uppercase tracking-widest text-mtm-teal-dark font-mono">{children}</span>
);

const MID_BAND_IMG = fileUrl("mtm-marine/site/falcon-band-running.webp", 2000);
const LOWER_BAND_IMG = fileUrl("mtm-marine/site/falcon-band-f215.webp", 2000);
const CTA_IMG = fileUrl("mtm-marine/site/falcon-band-road.webp", 2000);

export default function Home() {
  const [s, setS] = useState({});
  const [featured, setFeatured] = useState([]);
  const [brands, setBrands] = useState([]);
  const [authBrands, setAuthBrands] = useState([]);
  const [award, setAward] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data || {})).catch(() => {});
    api.get("/boats?featured=true").then((r) => setFeatured((r.data || []).filter((b) => b.status !== "sold").slice(0, 6))).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data || [])).catch(() => {});
    api.get("/authorized-brands").then((r) => setAuthBrands(r.data || [])).catch(() => {});
    api.get("/awards?placement=homepage").then((r) => setAward((r.data || [])[0] || null)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
    Promise.all([api.get("/boats?status=en_route"), api.get("/boats?status=coming_soon")])
      .then(([a, b]) => setIncoming([...(a.data || []), ...(b.data || [])])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="MTM Marine Sales & Service | Boat Dealer in Bristol, TN"
        description="MTM Marine Sales & Service, new Falcon Boats, Armada Pontoons, used boats, and certified marine service in Bristol, Tennessee. 2026 Falcon Boats Dealer of the Year."
        jsonLd={{
          "@context": "https://schema.org", "@type": "AutoDealer", name: "MTM Marine Sales & Service",
          telephone: s.phone || "423-878-3520",
          address: { "@type": "PostalAddress", streetAddress: s.address_line || "3525 US-421 Scenic", addressLocality: "Bristol", addressRegion: "TN", postalCode: "37620" },
        }}
      />

      {/* HERO */}
      <section className="relative bg-mtm-charcoal text-white overflow-hidden">
        <div className="absolute inset-0">
          {s.hero_image_path ? (
            <img src={fileUrl(s.hero_image_path, 1600)} alt="Falcon bass boat running on a Tennessee lake" className="w-full h-full object-cover" fetchPriority="high" loading="eager" decoding="async" />
          ) : (
            <ImageSlot label="MTM-Homepage-Hero-01.jpg" dimensions="2400 x 1200" aspect="h-full" className="h-full rounded-none border-0 bg-slate-800" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-mtm-purple-deep/95 via-mtm-purple-deep/70 to-mtm-purple-deep/20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-24 md:py-36 lg:py-44">
          <div className="max-w-2xl animate-fade-up">
            <Eyebrow>Bristol, Tennessee</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight font-heading leading-tight mt-3">
              {s.hero_headline || "MTM Marine Sales & Service"}
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mt-4 font-sans">
              {s.hero_subheadline || "Boat Sales & Marine Service in Bristol, Tennessee"}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button asChild size="lg" className="bg-mtm-teal hover:bg-mtm-teal-bright text-white rounded-full px-7 text-base" data-testid="hero-shop-inventory-button">
                <Link to="/inventory">{s.hero_primary_cta || "Shop Inventory"} <ArrowRight className="ml-1" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/40 text-white bg-white/5 hover:bg-white hover:text-mtm-purple-deep rounded-full px-7 text-base" data-testid="hero-schedule-service-button">
                <Link to="/service">{s.hero_secondary_cta || "Schedule Service"}</Link>
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-8 text-sm text-mtm-teal-bright font-semibold">
              <Award size={18} /> 2026 Falcon Boats Dealer of the Year
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED INVENTORY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14 sm:py-20 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <Eyebrow>In Stock Now</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading text-slate-900 mt-2">Featured Inventory</h2>
          </div>
          <Link to="/inventory" className="hidden sm:flex items-center gap-1 text-sm font-semibold text-mtm-purple hover:gap-2 transition-all" data-testid="view-all-inventory-link">
            View All Inventory <ArrowRight size={15} />
          </Link>
        </div>
        {featured.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((b, i) => <BoatCard key={b.id} boat={b} index={i} />)}
          </div>
        ) : (
          <p className="text-slate-500">No featured boats yet. <Link to="/inventory" className="text-mtm-purple underline">Browse all inventory</Link>.</p>
        )}
      </section>

      {/* SHOP OUR BOAT BRANDS */}
      <section className="bg-white border-y border-slate-200 py-14 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <Eyebrow>Our Lineup</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading text-slate-900 mt-2">Shop Our Boat Brands</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {brands.map((b) => (
              <div key={b.key} className="group bg-mtm-bg rounded-3xl border border-slate-200 overflow-hidden flex flex-col md:flex-row shadow-sm hover:shadow-xl transition-shadow duration-300" data-testid={`brand-feature-${b.key}`}>
                <div className="md:w-1/2 overflow-hidden">
                  {b.hero_image_path ? (
                    <img src={fileUrl(b.hero_image_path, 1000)} alt={b.name} className="w-full h-full object-cover aspect-[4/3] md:aspect-auto md:min-h-[340px] group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <ImageSlot label={b.hero_slot} dimensions="800 x 600" aspect="aspect-[4/3] md:aspect-auto md:h-full md:min-h-[340px]" className="rounded-none border-0" />
                  )}
                </div>
                <div className="p-8 md:p-10 md:w-1/2 flex flex-col justify-center">
                  {b.logo_path && (
                    <img src={fileUrl(b.logo_path, 400)} alt={`${b.name} logo`} className={`${b.key === "armada" ? "h-20" : "h-14"} w-auto object-contain self-start mb-4`} />
                  )}
                  <h3 className="font-heading font-bold text-2xl lg:text-3xl text-mtm-purple-deep">{b.name}</h3>
                  <p className="text-base text-slate-600 mt-3 flex-1 leading-relaxed">{b.short_description}</p>
                  <Button asChild size="lg" className="mt-6 self-start bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full px-6" data-testid={`explore-${b.key}-button`}>
                    <Link to={`/brands/${b.key}`}>Explore {b.name} <ChevronRight size={16} /></Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AWARD / DEALER OF THE YEAR */}
      {award && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12 sm:py-16 md:py-20">
          <div className="relative bg-gradient-to-br from-mtm-purple-deep via-mtm-purple to-mtm-purple-deep text-white rounded-3xl shadow-2xl ring-1 ring-white/10 p-10 md:p-14 flex flex-col md:flex-row items-center gap-10 overflow-hidden" data-testid="award-section">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-mtm-teal/15 blur-2xl pointer-events-none" />
            <div className="relative shrink-0 flex flex-col items-center">
              <div className="h-36 w-36 rounded-full bg-white/10 border-2 border-mtm-teal-bright/40 flex items-center justify-center">
                <Award size={68} className="text-mtm-teal-bright" />
              </div>
              <span className="mt-4 text-xs font-bold uppercase tracking-widest font-mono bg-mtm-teal-bright text-mtm-purple-deep px-3 py-1 rounded-full">2026 Award</span>
            </div>
            <div className="relative text-center md:text-left flex-1">
              <Eyebrow>Official Recognition</Eyebrow>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mt-3 leading-tight">{award.title}</h2>
              <p className="text-slate-200 mt-4 max-w-2xl text-base md:text-lg leading-relaxed">{award.description}</p>
              <div className="flex flex-wrap justify-center md:justify-start gap-6 mt-7 text-sm font-mono">
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-mtm-teal-bright" /> Certified Service</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-mtm-teal-bright" /> #1 Falcon Retailer</span>
                <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-mtm-teal-bright" /> Local & Trusted</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ABOUT MTM */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14 sm:py-20 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
        {s.about_image_path ? (
          <img src={fileUrl(s.about_image_path, 1200)} alt="MTM Marine Sales & Service, 2026 Falcon Boats Dealer of the Year" className="rounded-2xl w-full aspect-[4/3] object-cover shadow-lg" />
        ) : (
          <ImageSlot label="MTM-Dealership-Exterior-01.jpg" dimensions="1000 x 750" />
        )}
        <div>
          <Eyebrow>About MTM</Eyebrow>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading text-slate-900 mt-2 leading-tight">{s.about_headline || "A Local Marine Dealership Built on Trust"}</h2>
          <p className="text-slate-600 mt-5 text-base md:text-lg leading-relaxed">{s.about_body}</p>
          <Button asChild size="lg" variant="outline" className="mt-7 border-mtm-purple text-mtm-purple hover:bg-mtm-purple hover:text-white rounded-full px-6" data-testid="about-read-more-button">
            <Link to="/about">Learn More About Us <ArrowRight size={16} /></Link>
          </Button>
        </div>
      </section>

      {/* SALES & SERVICE */}
      <section className="bg-white border-y border-slate-200 py-14 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-3xl border border-slate-200 p-10 md:p-12 bg-mtm-bg" data-testid="boat-sales-block">
            <span className="h-14 w-14 rounded-2xl bg-mtm-purple/10 text-mtm-purple flex items-center justify-center mb-5"><Ship size={30} /></span>
            <h3 className="font-heading font-bold text-2xl lg:text-3xl text-slate-900">Boat Sales</h3>
            <p className="text-slate-600 mt-3 text-base md:text-lg leading-relaxed">{s.sales_body}</p>
            <Link to="/inventory" className="inline-flex items-center gap-1 mt-5 text-sm font-semibold text-mtm-purple hover:gap-2 transition-all">Browse Inventory <ArrowRight size={15} /></Link>
          </div>
          <div className="rounded-3xl border border-slate-200 p-10 md:p-12 bg-mtm-bg" data-testid="marine-service-block">
            <span className="h-14 w-14 rounded-2xl bg-mtm-teal/10 text-mtm-teal-dark flex items-center justify-center mb-5"><Wrench size={30} /></span>
            <h3 className="font-heading font-bold text-2xl lg:text-3xl text-slate-900">Marine Service</h3>
            <p className="text-slate-600 mt-3 text-base md:text-lg leading-relaxed">{s.service_body}</p>
            <Link to="/service" className="inline-flex items-center gap-1 mt-5 text-sm font-semibold text-mtm-teal-dark hover:gap-2 transition-all">Schedule Service <ArrowRight size={15} /></Link>
          </div>
        </div>
      </section>

      {/* AUTHORIZED BRANDS STRIP */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-100 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-slate-500 font-mono mb-10">Authorized Brands & Products</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-5">
            {authBrands.map((b) => (
              <div key={b.id} className={`bg-white rounded-xl border border-slate-200 h-24 flex items-center justify-center p-4 text-center shadow-sm ${b.name === "Sea Clear Power" ? "col-span-2 lg:col-span-2" : ""}`} data-testid={`authorized-brand-${b.id}`}>
                {b.logo_path ? (
                  <img src={fileUrl(b.logo_path, b.name === "Sea Clear Power" ? 520 : 240)} alt={b.name} className={`max-h-14 object-contain ${b.name === "Sea Clear Power" ? "max-w-[170px]" : "max-w-full"}`} />
                ) : (
                  <span className="text-xs font-mono font-semibold text-slate-500 uppercase tracking-wide">{b.name}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE PHOTOGRAPHIC BAND */}
      <section className="relative min-h-[360px] sm:min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden" data-testid="home-lifestyle-band">
        <img src={MID_BAND_IMG} alt="Falcon bass boat running across a misty Tennessee lake at sunrise" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-r from-mtm-purple-deep/85 via-mtm-purple-deep/55 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-xl text-white">
            <Eyebrow>Built for Tennessee Water</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mt-3 leading-tight">Built for the Water. Backed by MTM.</h2>
            <p className="text-slate-100 mt-4 text-base md:text-lg leading-relaxed">From the showroom to the boat ramp, we set you up with the right rig and keep it running season after season.</p>
            <Button asChild size="lg" className="mt-7 bg-mtm-teal hover:bg-mtm-teal-bright text-white rounded-full px-7" data-testid="lifestyle-band-cta">
              <Link to="/inventory">Explore Our Boats <ArrowRight className="ml-1" size={18} /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* COMING SOON / EN ROUTE */}
      {incoming.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <div className="mb-10">
            <Eyebrow>On The Way</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading text-slate-900 mt-2">Coming Soon & En Route</h2>
            <p className="text-slate-600 mt-3 text-base md:text-lg">New boats headed to Bristol. Reserve yours before it arrives.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {incoming.slice(0, 3).map((b, i) => <BoatCard key={b.id} boat={b} index={i} />)}
          </div>
        </section>
      )}

      {/* LOCATION BLOCK */}
      <section className="bg-mtm-charcoal text-white py-14 sm:py-20 md:py-24" data-testid="location-block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Eyebrow>Visit Us</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading mt-2 leading-tight">Your Bristol, TN Boat Dealership</h2>
            <p className="text-slate-300 mt-4 text-base md:text-lg">Minutes from South Holston Lake and Boone Lake. Stop by the showroom or reach our service team.</p>
            <div className="space-y-3 mt-6 text-sm md:text-base">
              <p className="flex items-start gap-3"><MapPin size={18} className="text-mtm-teal-bright mt-0.5" /> {s.address_line || "3525 US-421 Scenic"}, {s.city_state_zip || "Bristol, TN 37620"}</p>
              <a href={`tel:${(s.phone || "4238783520").replace(/[^0-9]/g, "")}`} className="flex items-center gap-3 hover:text-mtm-teal-bright"><Phone size={18} className="text-mtm-teal-bright" /> {s.phone || "423-878-3520"}</a>
              <div className="flex items-start gap-3"><Clock size={18} className="text-mtm-teal-bright mt-0.5" /><div>{(s.hours || []).map((h, i) => <div key={i}>{h.day}: {h.value}</div>)}</div></div>
            </div>
            <div className="flex gap-3 mt-7">
              <Button asChild size="lg" className="bg-mtm-teal hover:bg-mtm-teal-bright text-white rounded-full"><a href="https://maps.google.com/?q=3525+US-421+Scenic+Bristol+TN+37620" target="_blank" rel="noreferrer" data-testid="get-directions-button">Get Directions</a></Button>
              <Button asChild size="lg" variant="outline" className="border-white/40 text-white bg-transparent hover:bg-white hover:text-mtm-charcoal rounded-full"><Link to="/contact">Contact Us</Link></Button>
            </div>
          </div>
          <ImageSlot label="MTM-Location-Bristol-TN-Map.jpg" dimensions="900 x 600" className="bg-slate-800 border-slate-700 hidden" />
          <iframe
            title="MTM Marine Bristol TN map"
            src="https://www.google.com/maps?q=3525%20US-421%20Scenic%20Bristol%20TN%2037620&output=embed"
            className="w-full h-80 md:h-[26rem] rounded-2xl border border-slate-700"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="home-location-map"
          />
        </div>
      </section>

      {/* LOWER PHOTOGRAPHIC BAND */}
      <section className="relative min-h-[300px] sm:min-h-[360px] md:min-h-[460px] flex items-center justify-center text-center overflow-hidden" data-testid="home-lifestyle-band-lower">
        <img src={LOWER_BAND_IMG} alt="Falcon F21S bass boat with Yamaha power running on a Tennessee lake" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-mtm-charcoal/55" />
        <div className="relative max-w-3xl mx-auto px-6 text-white">
          <Eyebrow>Your Season Starts Here</Eyebrow>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mt-3 leading-tight">More Time on the Lake, Less Time Waiting</h2>
          <p className="text-slate-100 mt-4 text-base md:text-lg leading-relaxed">A family dealership that knows the water you boat on. New Falcon rigs, quality used boats, and a service team you can count on.</p>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <div className="text-center mb-12">
            <Eyebrow>Reviews</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-heading text-slate-900 mt-2">What Local Boaters Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid={`testimonial-${t.id}`}>
                <div className="flex gap-0.5 text-amber-400 mb-4">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={17} fill="currentColor" />)}</div>
                <p className="text-base text-slate-700 leading-relaxed">"{t.text}"</p>
                <p className="mt-5 text-base font-semibold text-slate-900">{t.author}</p>
                <p className="text-sm text-slate-500">{t.location}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="relative overflow-hidden" data-testid="home-cta-band">
        <img src={CTA_IMG} alt="Falcon bass boat under tow headed to the lake" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-r from-mtm-purple/90 to-mtm-teal-dark/85" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-14 sm:py-20 md:py-24 flex flex-col md:flex-row items-center justify-between gap-8 text-white">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading leading-tight">Ready to get on the water?</h2>
            <p className="text-slate-100 mt-2 text-base md:text-lg">Browse inventory or send us a message, we're here to help.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button asChild size="lg" className="bg-white text-mtm-purple hover:bg-slate-100 rounded-full px-7"><Link to="/inventory">Shop Inventory</Link></Button>
            <LeadDialog ctaType="General Inquiry" triggerLabel="Contact Us" variant="teal" size="lg" testId="cta-band-contact-button" />
          </div>
        </div>
      </section>
    </div>
  );
}
